import { acceptHMRUpdate, defineStore } from 'pinia'

import api from '@/lib/api'
import sorting from '@/lib/utils/sorting'
import { transformApiFormErrors, transformDepartment } from '@/lib/utils/transformEntries'

import { ORDER_TEMPLATE_MODES } from '@/settings/constants'
import { useRolesStore } from '@/stores/roles'
import {
  ApiInventoryThresholdPayload,
  Department,
  ApiError,
  ApiCreateInventoryThresholdValidationPayload,
  ApiEditInventoryThresholdValidationPayload,
} from '@/types'

type State = {
  modes: typeof ORDER_TEMPLATE_MODES
  isThresholdDepartmentsLoaded: boolean
  thresholdDepartments: Department[]
}
type Actions = {
  createInventoryThreshold(thresholdData: ApiInventoryThresholdPayload): Promise<void>
  editInventoryThreshold(thresholdData: ApiInventoryThresholdPayload): Promise<void>
  validateThreshold(
    thresholdData:
      | ApiCreateInventoryThresholdValidationPayload
      | ApiEditInventoryThresholdValidationPayload,
  ): Promise<{ ok: boolean }>
  getDepartments(params: {
    pageSize?: number
    company: string
    templateId?: string
    writeAccess?: boolean
  }): Promise<Department[]>
  getThresholdDepartments(params: {
    pageSize?: number
    company: string
    templateId?: string
    writeAccess?: boolean
  }): Promise<Department[]>
  resetState(): void
}

export const useInventoryThresholdsStore = defineStore<
  string,
  State,
  Record<string, never>,
  Actions
>('inventory-thresholds', {
  state: () => ({
    modes: ORDER_TEMPLATE_MODES,
    isThresholdDepartmentsLoaded: false,
    thresholdDepartments: [],
  }),

  actions: {
    async createInventoryThreshold(thresholdData) {
      try {
        await api.inventoryTracking.createInventoryThreshold(thresholdData)
      } catch (error) {
        const transformedError = error as unknown as ApiError
        transformedError.response.data = transformApiFormErrors(transformedError)
        throw transformedError
      }
    },

    async editInventoryThreshold(thresholdData) {
      try {
        await api.inventoryTracking.updateInventoryThreshold(thresholdData)
      } catch (error) {
        const transformedError = error as unknown as ApiError
        transformedError.response.data = transformApiFormErrors(transformedError)
        throw transformedError
      }
    },

    async validateThreshold(thresholdData) {
      try {
        await api.inventoryTracking.validateThreshold(thresholdData)
        return { ok: true }
      } catch (error) {
        return {
          ok: false,
          errors: transformApiFormErrors(error),
        }
      }
    },

    async getDepartments(params) {
      const response = await api.inventoryTracking.getDepartments({
        pageSize: 9999,
        ...params,
      })
      const { departments: rawDepartments = [] } = response.data
      const transformDepartments = rawDepartments.map(transformDepartment)
      const sortedDepartments = sorting.sortObjectsListByAlphaAsc(transformDepartments, 'name')
      return sortedDepartments
    },

    async getThresholdDepartments(params) {
      const rolesStore = useRolesStore()
      if (!rolesStore.isUserSuperAdmin && this.isThresholdDepartmentsLoaded) {
        return this.thresholdDepartments
      }
      const departments = await this.getDepartments(params)
      if (!rolesStore.isUserSuperAdmin) {
        this.thresholdDepartments = departments
        this.isThresholdDepartmentsLoaded = true
      }
      return departments
    },

    resetState() {
      this.$reset()
    },
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useInventoryThresholdsStore, import.meta.hot))
}
